import React, { useEffect, useState } from "react";
import {
  DndContext, DragOverlay, closestCenter, useSensor, useSensors, PointerSensor, KeyboardSensor,
} from "@dnd-kit/core";
import { sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { DragPreview } from "@/components/practice/LibraryItems";
import { AttendanceSection } from "@/components/practice/AttendanceSection";
import { PracticeDetailsForm, PeriodTimeline, EditorFooter, PracticeLibrary } from "@/components/practice/EditorSections";
import { usePracticeLibrary, usePracticeDnd } from "@/components/practice/usePracticeEditor";

const newPeriodId = () => `p-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
const withIds = (periods) => (periods || []).map(pd => pd.id ? pd : { ...pd, id: `p-${Math.random().toString(36).slice(2, 10)}` });
const normalize = (practice) => ({ ...practice, periods: withIds(practice.periods) });

function usePracticeActions(p, setSaving, onSaved, onDeleted) {
  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        title: p.title, date: p.date, start_time: p.start_time,
        location: p.location, weather: p.weather, objectives: p.objectives,
        attendance_expected: +p.attendance_expected || 0, periods: p.periods || [],
      };
      const { data } = p.id ? await api.put(`/practices/${p.id}`, payload) : await api.post(`/practices`, payload);
      onSaved(data);
      toast.success("Practice saved");
    } catch (e) {
      console.error("Practice save failed", e);
      toast.error("Save failed");
    } finally { setSaving(false); }
  };

  const del = async () => {
    if (!p.id || !window.confirm("Delete this practice?")) return;
    try {
      await api.delete(`/practices/${p.id}`);
      onDeleted(p.id);
      toast.success("Practice deleted");
    } catch (e) {
      console.error("Practice delete failed", e);
      toast.error("Delete failed");
    }
  };

  const print = () => {
    if (p.id) window.open(`/practice-planner/${p.id}/print`, "_blank");
    else toast.error("Save the practice first before printing");
  };
  return { save, del, print };
}

export function PracticeEditor({ practice, onSaved, onDeleted }) {
  const [p, setP] = useState(() => normalize(practice));
  const [saving, setSaving] = useState(false);
  const [libTab, setLibTab] = useState("plays");
  const library = usePracticeLibrary();

  useEffect(() => setP(normalize(practice)), [practice]);

  const update = (patch) => setP(prev => ({ ...prev, ...patch }));
  const periods = p.periods || [];
  const setPeriod = (i, np) => update({ periods: periods.map((x, j) => (j === i ? np : x)) });
  const addPeriod = () => update({
    periods: [...periods, { id: newPeriodId(), name: "New Period", duration: 10, coach: "", equipment: "", notes: "", play_ids: [], drill_ids: [] }],
  });
  const removePeriod = (i) => update({ periods: periods.filter((_, x) => x !== i) });
  const totalMinutes = periods.reduce((s, x) => s + (+x.duration || 0), 0);

  const { activeDrag, onDragStart, onDragEnd } = usePracticeDnd(periods, update);
  const { save, del, print } = usePracticeActions(p, setSaving, onSaved, onDeleted);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={onDragStart} onDragEnd={onDragEnd}>
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_280px] gap-6">
        <div className="bg-surface border border-surface-3">
          <PracticeDetailsForm p={p} update={update} />
          <PeriodTimeline p={p} plays={library.plays} drills={library.drills} totalMinutes={totalMinutes}
            addPeriod={addPeriod} setPeriod={setPeriod} removePeriod={removePeriod} />
          <AttendanceSection practiceId={p.id} saved={!!p.id} attendance={p.attendance || {}}
            onAttendanceChange={(att) => setP(prev => ({ ...prev, attendance: att }))} />
          <EditorFooter p={p} del={del} print={print} save={save} saving={saving} />
        </div>
        <PracticeLibrary libTab={libTab} setLibTab={setLibTab} {...library} />
      </div>
      <DragOverlay>
        <DragPreview activeDrag={activeDrag} />
      </DragOverlay>
    </DndContext>
  );
}
